import { useMemo, useState } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";

import PageChrome from "@/layouts/PageChrome.jsx";
import { Card, CardBody, CardExtra, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import Icon from "@/components/ui/Icon.jsx";
import { SearchInput, Select } from "@/components/ui/Form.jsx";
import { Callout, EmptyState, StatCard, Tabs } from "@/components/ui/Display.jsx";
import Pagination, { PaginationInfo } from "@/components/ui/Pagination.jsx";
import { SortableTh, TableWrap, Td, Th, Tr } from "@/components/ui/Table.jsx";
import Tag, { ProductivityTag } from "@/components/ui/Tag.jsx";
import { useConfirm, useToast } from "@/components/ui/feedback.js";
import RuleFormDrawer from "@/components/rules/RuleFormDrawer.jsx";
import OverridesDrawer from "@/components/rules/OverridesDrawer.jsx";
import ImportDrawer from "@/components/rules/ImportDrawer.jsx";

import { productivityRuleService, teamService } from "@/lib/api.js";
import { apiErrorMessage } from "@/lib/apiClient.js";
import { useAuth } from "@/context/auth.js";
import { downloadCsv } from "@/lib/csv.js";
import { nextSort, sortRows } from "@/lib/sort.js";
import {
    EMPTY_FILTERS,
    RULE_SORTS,
    filterRules,
    ruleStats,
    statusForTeam,
    validatePattern,
} from "@/lib/productivityRules.js";

/*
 * Replaces frontend/app/(dashboard)/productivity-rules/page.js.
 *
 * A wrong rule does not fail loudly — it silently mis-classifies every session
 * it touches, and an uncompilable regex classifies nothing while looking
 * perfectly healthy in the table. So this page does three things the one it
 * replaces does not: it tests patterns with the SAME matcher the server uses
 * (lib/productivityRules.js), it counts broken patterns as a headline number,
 * and it shows what each team would actually get.
 */

const NONE = [];

export default function ProductivityRules() {
    const queryClient = useQueryClient();
    const toast = useToast();
    const confirm = useConfirm();
    const { can } = useAuth();

    const [tab, setTab] = useState("rules");
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [sort, setSort] = useState({ by: "name", dir: "asc" });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const [editing, setEditing] = useState(null);
    const [overriding, setOverriding] = useState(null);
    const [importing, setImporting] = useState(false);
    const [formError, setFormError] = useState("");

    const [rulesQuery, teamsQuery] = useQueries({
        queries: [
            {
                queryKey: ["productivity-rules"],
                queryFn: () =>
                    productivityRuleService.getRules().then((r) => r.data?.data?.rules ?? []),
            },
            {
                queryKey: ["teams"],
                queryFn: () =>
                    teamService.getOrganizationTeams().then((r) => r.data?.data?.teams ?? []),
            },
        ],
    });

    const rules = rulesQuery.data ?? NONE;
    const teams = teamsQuery.data ?? NONE;

    const stats = useMemo(() => ruleStats(rules), [rules]);
    const categories = useMemo(
        () => [...new Set(rules.map((r) => r.category).filter(Boolean))].sort(),
        [rules],
    );

    const rows = useMemo(
        () => sortRows(filterRules(rules, filters), sort, RULE_SORTS),
        [rules, filters, sort],
    );

    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    const current = Math.min(page, totalPages);
    const visible = rows.slice((current - 1) * pageSize, current * pageSize);

    const setFilter = (key, value) => {
        setFilters((f) => ({ ...f, [key]: value }));
        setPage(1);
    };

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: ["productivity-rules"] });

    const saveRule = useMutation({
        mutationFn: (values) =>
            editing?._id
                ? productivityRuleService.updateRule(editing._id, values)
                : productivityRuleService.createRule(values),
        onSuccess: (_res, values) => {
            toast(`${values.name} ${editing?._id ? "updated" : "created"}`);
            setEditing(null);
            setFormError("");
            invalidate();
        },
        onError: (error) =>
            setFormError(apiErrorMessage(error, "Could not save this rule. Please try again.")),
    });

    const saveOverrides = useMutation({
        mutationFn: (teamRules) =>
            productivityRuleService.updateRule(overriding._id, { teamRules }),
        onSuccess: (_res, teamRules) => {
            toast(
                teamRules.length
                    ? `${overriding.name}: ${teamRules.length} team ${teamRules.length === 1 ? "override" : "overrides"}`
                    : `${overriding.name}: every team follows the default`,
            );
            setOverriding(null);
            invalidate();
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not save the overrides."), "error"),
    });

    const removeRule = useMutation({
        mutationFn: (rule) => productivityRuleService.deleteRule(rule._id),
        onSuccess: (_res, rule) => {
            toast(`${rule.name} deleted`, "error");
            invalidate();
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not delete this rule."), "error"),
    });

    const runImport = useMutation({
        mutationFn: (list) => productivityRuleService.bulkImport({ rules: list }),
        onSuccess: (_res, list) => {
            toast(`${list.length} ${list.length === 1 ? "rule" : "rules"} imported`);
            setImporting(false);
            setFormError("");
            invalidate();
        },
        onError: (error) => setFormError(apiErrorMessage(error, "The import failed.")),
    });

    const askDelete = async (rule) => {
        const ok = await confirm({
            tone: "danger",
            title: `Delete ${rule.name}?`,
            body: (
                <>
                    <p>
                        Sessions matching{" "}
                        <code className="rounded bg-slate-100 px-1 font-mono text-[12px] dark:bg-slate-800">
                            {rule.pattern}
                        </code>{" "}
                        stop being classified and fall through to Unrated.
                    </p>
                    <p className="mt-1.5">
                        Already-recorded activity keeps the status it was given at the time.
                    </p>
                </>
            ),
            okLabel: "Delete rule",
        });
        if (ok) removeRule.mutate(rule);
    };

    const exportRules = () =>
        downloadCsv(
            "productivity-rules",
            ["Name", "Type", "Pattern", "Regex", "Default status", "Category", "Team overrides"],
            rules.map((r) => [
                r.name,
                r.type,
                r.pattern,
                r.isRegex ? "yes" : "no",
                r.globalStatus,
                r.category ?? "",
                (r.teamRules ?? []).length,
            ]),
        );

    const loading = rulesQuery.isPending;

    return (
        <>
            <PageChrome breadcrumb={["Productivity Rules"]} title="Productivity Rules">
                <>
                    <Button onClick={exportRules} disabled={!rules.length} data-export>
                        <Icon name="download" className="size-4" />
                        Export
                    </Button>
                    {can("manageOrg") && (
                        <>
                            <Button onClick={() => setImporting(true)} data-import>
                                Import
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    setFormError("");
                                    setEditing({});
                                }}
                                data-add-rule
                            >
                                <Icon name="shield" className="size-4" />
                                New rule
                            </Button>
                        </>
                    )}
                </>
            </PageChrome>

            <main className="space-y-5 p-4 sm:p-5">
                <div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Productivity Rules</h1>
                    <p className="mt-0.5 text-[13px] text-slate-500 dark:text-slate-400">
                        What counts as productive. Anything no rule matches is recorded as Unrated.
                    </p>
                </div>

                {rulesQuery.isError && (
                    <Callout tone="danger">
                        {apiErrorMessage(rulesQuery.error, "Failed to load rules.")}{" "}
                        <button
                            type="button"
                            onClick={() => rulesQuery.refetch()}
                            className="font-semibold underline"
                        >
                            Try again
                        </button>
                    </Callout>
                )}

                {/* A rule whose pattern cannot compile matches nothing, and looks
                    entirely normal in the table. Say so at the top. */}
                {stats.broken > 0 && (
                    <Callout tone="danger" data-broken-warning>
                        <strong className="font-semibold">
                            {stats.broken} {stats.broken === 1 ? "rule has" : "rules have"} a pattern
                            that cannot compile
                        </strong>{" "}
                        — {stats.brokenRules.map((r) => r.name).join(", ")}. They match nothing at
                        all, so the sessions they were meant to cover are being recorded as Unrated.
                    </Callout>
                )}

                <section className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
                    <StatCard
                        tone="brand"
                        label="Rules"
                        value={stats.total}
                        hint={`${categories.length} categories`}
                        icon={<Icon name="shield" className="size-4" />}
                    />
                    <StatCard
                        tone="emerald"
                        label="Productive"
                        value={stats.productive}
                        hint="Default status"
                        icon={<Icon name="check" className="size-4" />}
                        onClick={() => setFilter("status", "productive")}
                        active={filters.status === "productive"}
                    />
                    <StatCard
                        tone="rose"
                        label="Unproductive"
                        value={stats.unproductive}
                        hint="Default status"
                        icon={<Icon name="xCircle" className="size-4" />}
                        onClick={() => setFilter("status", "unproductive")}
                        active={filters.status === "unproductive"}
                    />
                    <StatCard
                        tone="violet"
                        label="With overrides"
                        value={stats.withOverrides}
                        hint="A team is treated differently"
                        icon={<Icon name="team" className="size-4" />}
                    />
                </section>

                <Card>
                    <CardHead>
                        <CardTitle>
                            <Tabs
                                value={tab}
                                onChange={setTab}
                                options={[
                                    { value: "rules", label: "Rules" },
                                    { value: "teams", label: "By team" },
                                ]}
                            />
                        </CardTitle>
                        {tab === "rules" && (
                            <CardExtra>
                                <SearchInput
                                    value={filters.search}
                                    onChange={(e) => setFilter("search", e.target.value)}
                                    placeholder="Search name or pattern…"
                                    aria-label="Search rules"
                                    className="w-full sm:w-60"
                                />
                                <Select
                                    size="sm"
                                    value={filters.type}
                                    onChange={(e) => setFilter("type", e.target.value)}
                                    aria-label="Filter by type"
                                    className="w-auto"
                                >
                                    <option value="">All types</option>
                                    <option value="domain">Domain</option>
                                    <option value="website">Website</option>
                                    <option value="application">Application</option>
                                </Select>
                                <Select
                                    size="sm"
                                    value={filters.category}
                                    onChange={(e) => setFilter("category", e.target.value)}
                                    aria-label="Filter by category"
                                    className="w-auto"
                                >
                                    <option value="">All categories</option>
                                    {categories.map((c) => (
                                        <option key={c} value={c}>
                                            {c}
                                        </option>
                                    ))}
                                </Select>
                            </CardExtra>
                        )}
                    </CardHead>

                    <CardBody>
                        {tab === "rules" ? (
                            <>
                                <TableWrap minWidth="960px">
                                    <thead>
                                        <tr>
                                            <SortableTh
                                                field="name"
                                                sort={sort}
                                                onSort={(f) => setSort((s) => nextSort(s, f))}
                                            >
                                                Rule
                                            </SortableTh>
                                            <SortableTh
                                                field="type"
                                                sort={sort}
                                                onSort={(f) => setSort((s) => nextSort(s, f))}
                                                className="w-32"
                                            >
                                                Type
                                            </SortableTh>
                                            <SortableTh
                                                field="pattern"
                                                sort={sort}
                                                onSort={(f) => setSort((s) => nextSort(s, f))}
                                            >
                                                Pattern
                                            </SortableTh>
                                            <SortableTh
                                                field="status"
                                                sort={sort}
                                                onSort={(f) => setSort((s) => nextSort(s, f))}
                                                className="w-36"
                                            >
                                                Default
                                            </SortableTh>
                                            <SortableTh
                                                field="overrides"
                                                sort={sort}
                                                onSort={(f) => setSort((s) => nextSort(s, f, "desc"))}
                                                className="w-32"
                                            >
                                                Overrides
                                            </SortableTh>
                                            <Th last className="w-28 text-right">
                                                Actions
                                            </Th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {visible.map((rule) => {
                                            const valid = validatePattern(rule.pattern, rule.isRegex);
                                            return (
                                                <Tr key={rule._id} data-rule={rule._id}>
                                                    <Td>
                                                        <span className="block truncate font-medium">
                                                            {rule.name}
                                                        </span>
                                                        {rule.category && (
                                                            <span className="block truncate text-[11.5px] text-slate-400 dark:text-slate-500">
                                                                {rule.category}
                                                            </span>
                                                        )}
                                                    </Td>

                                                    <Td>
                                                        <Tag tone="slate">{rule.type}</Tag>
                                                    </Td>

                                                    <Td>
                                                        <span className="flex min-w-0 items-center gap-1.5">
                                                            <code className="min-w-0 truncate rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[12px] dark:bg-slate-800">
                                                                {rule.pattern}
                                                            </code>
                                                            {rule.isRegex && (
                                                                <Tag tone="violet">regex</Tag>
                                                            )}
                                                            {!valid.ok && (
                                                                <Tag tone="rose" data-broken={rule._id}>
                                                                    Broken
                                                                </Tag>
                                                            )}
                                                        </span>
                                                    </Td>

                                                    <Td>
                                                        <ProductivityTag status={rule.globalStatus} />
                                                    </Td>

                                                    <Td>
                                                        {(rule.teamRules ?? []).length ? (
                                                            <Button
                                                                variant="link"
                                                                onClick={() => setOverriding(rule)}
                                                                data-overrides={rule._id}
                                                            >
                                                                {rule.teamRules.length} team
                                                                {rule.teamRules.length === 1 ? "" : "s"}
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="link"
                                                                onClick={() => setOverriding(rule)}
                                                                data-overrides={rule._id}
                                                                className="text-slate-400 dark:text-slate-500"
                                                            >
                                                                Add
                                                            </Button>
                                                        )}
                                                    </Td>

                                                    <Td last className="text-right">
                                                        {can("manageOrg") && (
                                                            <span className="flex items-center justify-end gap-0.5">
                                                                <Button
                                                                    variant="icon"
                                                                    aria-label={`Edit ${rule.name}`}
                                                                    onClick={() => {
                                                                        setFormError("");
                                                                        setEditing(rule);
                                                                    }}
                                                                    data-edit={rule._id}
                                                                >
                                                                    <Icon name="cog" className="size-4" />
                                                                </Button>
                                                                <Button
                                                                    variant="icon"
                                                                    aria-label={`Delete ${rule.name}`}
                                                                    onClick={() => askDelete(rule)}
                                                                    data-delete={rule._id}
                                                                    className="text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40"
                                                                >
                                                                    <Icon name="xCircle" className="size-4" />
                                                                </Button>
                                                            </span>
                                                        )}
                                                    </Td>
                                                </Tr>
                                            );
                                        })}

                                        {!visible.length && (
                                            <tr>
                                                <td colSpan={6}>
                                                    <EmptyState
                                                        title={
                                                            loading
                                                                ? "Loading…"
                                                                : rules.length
                                                                  ? "No rule matches these filters"
                                                                  : "No rules yet"
                                                        }
                                                        hint={
                                                            rules.length
                                                                ? "Try clearing the type or category filter."
                                                                : "Without rules, every session is recorded as Unrated."
                                                        }
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </TableWrap>

                                <div className="mt-3.5 flex flex-wrap items-center gap-3">
                                    <PaginationInfo
                                        page={current}
                                        pageSize={pageSize}
                                        total={rows.length}
                                        noun="rules"
                                        sizes={[20, 50, 100]}
                                        onPageSizeChange={(n) => {
                                            setPageSize(n);
                                            setPage(1);
                                        }}
                                    />
                                    <div className="ml-auto">
                                        <Pagination
                                            page={current}
                                            totalPages={totalPages}
                                            onChange={setPage}
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <ByTeam rules={rules} teams={teams} />
                        )}
                    </CardBody>
                </Card>
            </main>

            {editing && (
                <RuleFormDrawer
                    key={editing._id ?? "new"}
                    rule={editing._id ? editing : null}
                    rules={rules}
                    onClose={() => {
                        setEditing(null);
                        setFormError("");
                    }}
                    onSubmit={(values) => saveRule.mutate(values)}
                    saving={saveRule.isPending}
                    error={formError}
                />
            )}

            {overriding && (
                <OverridesDrawer
                    key={overriding._id}
                    rule={overriding}
                    teams={teams}
                    onClose={() => setOverriding(null)}
                    onSave={(teamRules) => saveOverrides.mutate(teamRules)}
                    saving={saveOverrides.isPending}
                />
            )}

            {importing && (
                <ImportDrawer
                    existing={rules}
                    onClose={() => {
                        setImporting(false);
                        setFormError("");
                    }}
                    onImport={(list) => runImport.mutate(list)}
                    saving={runImport.isPending}
                    error={formError}
                />
            )}
        </>
    );
}

/* ---- Effective status per team ------------------------------------------ */
function ByTeam({ rules, teams }) {
    if (!teams.length) {
        return (
            <EmptyState
                title="No teams yet"
                hint="Overrides let one team treat an app differently from everyone else."
            />
        );
    }

    return (
        <>
            <p className="mb-3.5 text-[12.5px] text-slate-500 dark:text-slate-400">
                What each team actually gets. A cell in bold is an override; the rest follow the
                rule's default.
            </p>

            <TableWrap minWidth={`${320 + teams.length * 140}px`}>
                <thead>
                    <tr>
                        <Th first>Rule</Th>
                        <Th className="w-32">Default</Th>
                        {teams.map((team) => (
                            <Th key={team._id} className="w-36">
                                {team.name}
                            </Th>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    {rules.map((rule) => (
                        <Tr key={rule._id} data-team-row={rule._id}>
                            <Td first>
                                <span className="block truncate font-medium">{rule.name}</span>
                                <code className="block truncate font-mono text-[11px] text-slate-400 dark:text-slate-500">
                                    {rule.pattern}
                                </code>
                            </Td>

                            <Td>
                                <ProductivityTag status={rule.globalStatus} />
                            </Td>

                            {teams.map((team) => {
                                const { status, viaOverride } = statusForTeam(rule, team._id);
                                return (
                                    <Td key={team._id} data-cell={`${rule._id}:${team._id}`}>
                                        <span
                                            className={viaOverride ? "font-semibold" : "opacity-60"}
                                            data-override={viaOverride ? "yes" : "no"}
                                        >
                                            <ProductivityTag status={status} />
                                        </span>
                                    </Td>
                                );
                            })}
                        </Tr>
                    ))}

                    {!rules.length && (
                        <tr>
                            <td colSpan={2 + teams.length}>
                                <EmptyState title="No rules yet" />
                            </td>
                        </tr>
                    )}
                </tbody>
            </TableWrap>
        </>
    );
}
